import React from 'react';
import { string, bool } from 'prop-types';
import clsx from 'clsx';
import styles from './BingoSquare.module.scss';

const LAYOUTS = {
  BINGO_90: 'Bingo90Layout',
  BINGO_30: 'Bingo30Layout',
  BINGO_4X4: ['Bingo40Layout', 'Bingo60Layout', 'Bingo80Layout'],
};

const PATTERNS = {
  INNER_SQUARE: [6, 7, 8, 11, 12, 13, 16, 17, 18],
  ONE_ROW_TOP: [0, 1, 2, 3, 4],
  ONE_ROW: [10, 11, 12, 13, 14],
  ONE_ROW_30: [3, 4, 5],
  ONE_ROW_VERTICAL: [2, 7, 12, 17, 22],
  ONE_ROW_VERTICAL_30: [1, 4, 7],
  ONE_ROW_VERTICAL_4X4: [0, 4, 8, 12],
  ONE_ROW_CROSS: [0, 6, 12, 18, 24],
  ONE_ROW_CROSS_30: [0, 4, 8],
  ONE_ROW_CROSS_4X4: [0, 5, 10, 15],
  FOUR_CORNERS_4X4: [0, 3, 12, 15],
  DIAMOND: [2, 6, 7, 8, 10, 11, 12, 13, 14, 16, 17, 18, 22],
  M_SYMBOL: [0, 4, 5, 6, 8, 9, 10, 12, 14, 15, 19, 20, 24],
  PLUS: [1, 2, 3, 5, 6, 8, 9, 10, 14, 15, 16, 18, 19, 21, 22, 23],
};

const getTotalFields = (layout) => {
  if (layout === LAYOUTS.BINGO_90) return 15;
  if (layout === LAYOUTS.BINGO_30) return 9;
  if (LAYOUTS.BINGO_4X4.includes(layout)) return 16;
  return 25;
};

const isFilled = (type, layout, index) => {
  const isLongTicket = layout === LAYOUTS.BINGO_90;
  const isTinyTickets = layout === LAYOUTS.BINGO_30;
  const is4x4Tickets = LAYOUTS.BINGO_4X4.includes(layout);
  const is3x3Tickets = isTinyTickets && !is4x4Tickets;

  switch (type) {
    case 'fullcard':
    case 'fullcard1TG':
    case 'fullcard2TG':
      return true;
    case '1rowhor':
      if (isLongTicket)
        return index <= 5 && PATTERNS.ONE_ROW_TOP.includes(index);
      if (is3x3Tickets) return PATTERNS.ONE_ROW_30.includes(index);
      if (is4x4Tickets) return index < 4;
      return index >= 5 && PATTERNS.ONE_ROW.includes(index);
    case '2rowhor':
      if (is3x3Tickets) return index < 6;
      if (is4x4Tickets) return index < 8;
      return index < 10;
    case '3rowhor':
      if (is4x4Tickets) return index < 12;
      return index < 15;
    case '4rowhor':
      if (is4x4Tickets) return index < 16;
      return index < 20;
    case 'square':
      return !PATTERNS.INNER_SQUARE.includes(index);
    case 'innerSquare':
      return PATTERNS.INNER_SQUARE.includes(index);
    case 'diamond':
      return PATTERNS.DIAMOND.includes(index);
    case '1rowvert':
      if (is3x3Tickets) return PATTERNS.ONE_ROW_VERTICAL_30.includes(index);
      if (is4x4Tickets) return PATTERNS.ONE_ROW_VERTICAL_4X4.includes(index);
      return PATTERNS.ONE_ROW_VERTICAL.includes(index);
    case '1rowdiag':
      if (is3x3Tickets) return PATTERNS.ONE_ROW_CROSS_30.includes(index);
      if (is4x4Tickets) return PATTERNS.ONE_ROW_CROSS_4X4.includes(index);
      return PATTERNS.ONE_ROW_CROSS.includes(index);
    case '4cor':
      return is4x4Tickets && PATTERNS.FOUR_CORNERS_4X4.includes(index);
    case 'm':
      return PATTERNS.M_SYMBOL.includes(index);
    case 'plus':
      return PATTERNS.PLUS.includes(index);
    default:
      return false;
  }
};

function BingoSquare({
  layout,
  type,
  isDark,
  isVertical,
  isGolden,
  isPotBoosterActive,
}) {
  const totalFields = getTotalFields(layout);

  return (
    <div
      className={clsx(styles.wrapper, {
        [styles.isVertical]: isVertical,
        [styles.is9Fields]: totalFields === 9,
        [styles.isSmallSquare]: totalFields === 15,
        [styles.is4x4]: totalFields === 16,
      })}
    >
      {Array.from({ length: totalFields }).map((_, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={`square-${layout}-${type}-${i}`}
          className={clsx(styles.field, {
            [styles.isVertical]: isVertical,
            [styles.is9Fields]: totalFields === 9,
            [styles.is4x4]: totalFields === 16,
            [styles.darker]: isDark,
            [styles.filled]: isFilled(type, layout, i),
            [styles.gold]: isGolden,
            [styles.isPotBoosterActive]: isPotBoosterActive,
          })}
        />
      ))}
    </div>
  );
}

BingoSquare.propTypes = {
  layout: string,
  type: string.isRequired,
  isDark: bool,
  isVertical: bool,
  isGolden: bool,
  isPotBoosterActive: bool,
};

BingoSquare.defaultProps = {
  layout: '',
  isDark: false,
  isVertical: false,
  isGolden: false,
  isPotBoosterActive: false,
};

export default BingoSquare;
